export default (element: HTMLElement) => {
  const buttonElement = element.querySelector<HTMLButtonElement>(
    '.expandablePill__button',
  );

  if (!buttonElement) {
    console.error(
      'Missing .expandablePill__button in expandablePill component',
    );
    return;
  }

  const button = buttonElement; // Now TypeScript knows this is non-null

  const expandedContentElement = element.querySelector<HTMLDivElement>(
    '.expandablePill__expandedContent',
  );

  if (!expandedContentElement) {
    console.error(
      'Missing .expandablePill__expandedContent in expandablePill component',
    );
    return;
  }

  const expandedContent = expandedContentElement; // Now TypeScript knows this is non-null

  const supportsPopover = 'popover' in HTMLElement.prototype;
  let isOpen = false;

  // Configure popover mode or fallback
  if (supportsPopover) {
    expandedContent.popover = 'manual';
    expandedContent.style.position = 'fixed';
  } else {
    expandedContent.style.display = 'none';
  }
  element.classList.add('is-loaded');

  // Position expanded content for popover mode
  function positionExpandedContent() {
    if (supportsPopover) {
      const rect = button.getBoundingClientRect();
      expandedContent.style.top = `${rect.bottom + 4}px`;
      expandedContent.style.left = `${rect.right}px`;
    }
    expandedContent.style.setProperty(
      '--expandablePill-width',
      `${button.offsetWidth}px`,
    );
  }

  // Open expanded content
  function open() {
    if (!isOpen) {
      isOpen = true;
      positionExpandedContent();

      if (supportsPopover) {
        try {
          expandedContent.showPopover();
        } catch (_error) {
          // Fallback for browsers without Popover API
          expandedContent.style.display = 'block';
        }
      } else {
        element.classList.add('is-expanded');
      }

      // Add listeners when opening
      setTimeout(() => {
        document.addEventListener('click', handleOutsideClick);
        document.addEventListener('keydown', handleEscape);
      }, 0);
    }
  }

  // Close expanded content
  function close() {
    isOpen = false;

    if (supportsPopover) {
      try {
        expandedContent.hidePopover();
      } catch (_error) {
        // Fallback for browsers without Popover API
        expandedContent.style.display = 'none';
      }
    } else {
      element.classList.remove('is-expanded');
    }

    // Remove listeners when closing
    document.removeEventListener('click', handleOutsideClick);
    document.removeEventListener('keydown', handleEscape);
  }

  // Toggle expanded content
  const toggle = () => {
    if (isOpen) {
      close();
    } else {
      open();
    }
  };

  const handleOutsideClick = (event: MouseEvent) => {
    if (!element.contains(event.target as Node)) {
      close();
    }
  };

  const handleEscape = (event: KeyboardEvent) => {
    if (event.key === 'Escape' && isOpen) {
      close();
    }
  };

  button.addEventListener('click', () => {
    toggle();
  });

  // Add scroll/resize listeners for popover mode
  if (supportsPopover) {
    window.addEventListener(
      'scroll',
      () => {
        if (isOpen) {
          positionExpandedContent();
        }
      },
      true,
    );

    window.addEventListener('resize', () => {
      if (isOpen) {
        positionExpandedContent();
      }
    });
  }

  // Clean up listeners if component is removed
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.removedNodes.forEach((node) => {
        if (node === element) {
          document.removeEventListener('click', handleOutsideClick);
          document.removeEventListener('keydown', handleEscape);
          observer.disconnect();
        }
      });
    });
  });

  if (element.parentElement) {
    observer.observe(element.parentElement, { childList: true });
  }
};
